import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  Label,
  Modal,
  Notification,
  TextInput,
  TextArea,
  PhoneNumberInput,
} from 'shared/components';
import {
  validateRequiredValue,
  validateEmail,
  debouncedValidateEmail,
  validateJSON,
  debouncedValidateJSON,
} from 'shared/validation';
import { useDispatch } from 'react-redux';
import { updateCustomerAccount } from '../actions';
import './EditCustomerAccountModal.scss';

const EditCustomerAccountModal = ({ closeCb, customerAccount, confirmCb }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [isDirtyFormAlertDisplayed, setDirtyFormAlertDisplay] = useState(false);

  const [formData, setFormData] = useState({
    name: {
      value: get(customerAccount, 'name', ''),
      error: '',
    },
    code: {
      value: get(customerAccount, 'code', ''),
      error: '',
    },
    description: {
      value: get(customerAccount, 'description', ''),
      error: '',
    },
    reference: {
      value: get(customerAccount, 'reference', ''),
      error: '',
    },
    email: {
      value: get(customerAccount, 'email', ''),
      error: '',
    },
    phoneNumber: {
      value: get(customerAccount, 'phone', ''),
      error: '',
    },
    address: {
      value: get(customerAccount, 'address', ''),
      error: '',
    },
    metadata: get(customerAccount, 'metadata')
      ? JSON.stringify(get(customerAccount, 'metadata'), null, 2)
      : '',
    metadataError: '',
  });

  const updateField = (field, updates) => {
    setFormData((prev) => ({
      ...prev,
      [field]:
        typeof updates === 'function'
          ? updates(prev[field])
          : { ...prev[field], ...updates },
    }));
  };

  const validateValue = async (field, val) => {
    setLoading(true);
    let errors;
    try {
      errors = await validateRequiredValue(val);
      updateField(field, { error: errors });
    } catch (err) {
      errors = t('Validation failed');
      updateField(field, { error: errors });
    }
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const validateEmailValue = async (field, val) => {
    if (!val) {
      updateField(field, { error: '' });
      return true;
    }
    setLoading(true);
    let errors;
    try {
      errors = await validateEmail(val);
      updateField(field, { error: errors });
    } catch (err) {
      errors = t('Validation failed');
      updateField(field, { error: errors });
    }
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const validateMetadata = async (val) => {
    setLoading(true);
    let errors;
    try {
      errors = await validateJSON(val);
      setFormData((prev) => ({ ...prev, metadataError: errors }));
    } catch (err) {
      errors = t('Validation failed');
      setFormData((prev) => ({ ...prev, metadataError: errors }));
    }
    setLoading(false);
    if (errors) {
      return false;
    }
    return true;
  };

  const validateFormData = async () => {
    const isNameValid = await validateValue('name', formData.name.value);
    const isEmailValid = await validateEmailValue(
      'email',
      formData.email.value,
    );
    const metadataValid = await validateMetadata();

    return isNameValid && isEmailValid && metadataValid;
  };

  const editCustomerAccount = async (customerAccountID, data) => {
    try {
      await dispatch(updateCustomerAccount(customerAccountID, data));
      setLoading(false);
      Notification('success', t('Changes saved successfully'));
      confirmCb();
      closeCb();
    } catch (err) {
      setLoading(false);
      Notification(
        'error',
        t('Your changes were not saved'),
        t('There was an error while saving your changes'),
      );
    }
  };

  const handleSubmit = async () => {
    if (!isDirty) {
      closeCb();
      return false;
    }
    const isValid = await validateFormData();
    if (!isValid || isLoading) {
      return false;
    }

    setLoading(true);
    const customerID = get(customerAccount, 'id');

    let parsedMetadata = {};
    if (formData.metadata) {
      try {
        parsedMetadata = JSON.parse(formData.metadata);
      } catch (err) {
        setLoading(false);
        Notification(
          'error',
          t('Invalid JSON format'),
          t('Metadata must be valid JSON'),
        );
        return false;
      }
    }

    const data = {
      reference: formData.reference.value,
      description: formData.description.value,
      email: formData.email.value,
      phone: formData.phoneNumber.value,
      address: formData.address.value,
      metadata: parsedMetadata,
    };

    await editCustomerAccount(customerID, data);
    return true;
  };

  const handleClose = () => {
    if (!isDirty) {
      return closeCb();
    }
    return setDirtyFormAlertDisplay(true);
  };

  const title = `${t('Edit customer account')} - ${get(customerAccount, 'name')}`;

  return (
    <Modal
      closeCb={handleClose}
      confirmCb={handleSubmit}
      disabled={isLoading}
      title={title}
      size="lg"
    >
      <div className="EditCustomerAccountModal">
        <form onSubmit={handleSubmit}>
          <div className="form-inner">
            <div className="left">
              <div>
                <Label text={t('Name')} inputId="customer-comp-name" />
                <TextInput
                  disabled={true}
                  id="customer-comp-name"
                  value={formData.name.value}
                  error={formData.name.error}
                />
              </div>
              <div>
                <Label text={t('Code')} inputId="customer-comp-code" />
                <TextInput
                  disabled={true}
                  id="customer-comp-code"
                  value={formData.code.value}
                  error={formData.code.error}
                />
              </div>
              <div>
                <Label text={t('Email')} inputId="customer-comp-email" />
                <TextInput
                  id="customer-comp-email"
                  value={formData.email.value}
                  error={formData.email.error}
                  handleChange={(val) => {
                    setDirty(true);
                    updateField('email', { value: val });
                    debouncedValidateEmail(val).then((err) =>
                      updateField('email', { error: err }),
                    );
                  }}
                />
              </div>
              <div>
                <Label
                  text={t('Reference')}
                  inputId="customer-comp-reference"
                />
                <TextInput
                  id="customer-comp-reference"
                  value={formData.reference.value}
                  error={formData.reference.error}
                  handleChange={(val) => {
                    setDirty(true);
                    updateField('reference', { value: val });
                  }}
                />
              </div>
            </div>
            <div className="divider active" />
            <div className="right">
              <div>
                <Label text={t('Address')} inputId="customer-comp-address" />
                <TextInput
                  id="customer-comp-address"
                  value={formData.address.value}
                  error={formData.address.error}
                  handleChange={(val) => {
                    setDirty(true);
                    updateField('address', { value: val });
                  }}
                />
              </div>
              <div>
                <Label
                  text={t('Phone Number')}
                  inputId="customer-comp-phonenumber"
                />
                <PhoneNumberInput
                  id="customer-comp-phonenumber"
                  value={formData.phoneNumber.value}
                  error={formData.phoneNumber.error}
                  handleChange={(val) => {
                    setDirty(true);
                    updateField('phoneNumber', { value: val });
                  }}
                />
              </div>
              <div>
                <Label
                  text={t('Description')}
                  inputId="customer-comp-description"
                />
                <TextArea
                  id="customer-comp-description"
                  rows="4"
                  value={formData.description.value}
                  error={formData.description.error}
                  handleChange={(val) => {
                    setDirty(true);
                    updateField('description', { value: val });
                  }}
                />
              </div>
              <div>
                <Label inputId="metadata-input" text={t('Metadata JSON')} />
                <TextArea
                  handleChange={(val) => {
                    setDirty(true);
                    setFormData((prev) => ({ ...prev, metadata: val }));
                    debouncedValidateJSON(val).then((err) =>
                      setFormData((prev) => ({ ...prev, metadataError: err })),
                    );
                  }}
                  id="metadata-input"
                  type="metadata"
                  value={formData.metadata}
                  error={formData.metadataError}
                  rows="4"
                />
              </div>
            </div>
          </div>
        </form>
        {isDirtyFormAlertDisplayed && (
          <DirtyFormAlert
            dirty={isDirty}
            closeAlert={() => setDirtyFormAlertDisplay(false)}
            closeCb={closeCb}
          />
        )}
      </div>
    </Modal>
  );
};

EditCustomerAccountModal.propTypes = {
  closeCb: PropTypes.func.isRequired,
  customerAccount: PropTypes.object.isRequired,
  confirmCb: PropTypes.func.isRequired,
};

export default EditCustomerAccountModal;
